import React, { useState, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { GET_FABRIC_GROUPS } from '@/containers/Fabrics/fabricQueries'
import {
  CREATE_FABRIC_PALETTE,
  UPDATE_FABRIC_PALETTE,
  GET_FABRIC_PALETTES,
  GET_FABRIC_PALETTE,
} from '../fabricPaletteQueries'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { ColumnDef } from '@tanstack/react-table'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'

interface FabricPaletteFormProps {
  data?: any
  onClose?: () => void
}

interface FabricGroup {
  id: number
  name: string
  fabric_group: {
    id: number
    code: string
    fabric_group_profiles: { name: string; language: string }[]
  }
}

interface EditableNameInputProps {
  groupId: number
  initialValue: string
  onNameChange: (groupId: number, value: string) => void
}

const EditableNameInput: React.FC<EditableNameInputProps> = ({
  groupId,
  initialValue,
  onNameChange,
}) => {
  const [value, setValue] = useState(initialValue)

  // Update local state when initialValue changes (e.g., when data loads)
  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    onNameChange(groupId, newValue)
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder="Enter custom name"
      className="min-w-[200px]"
    />
  )
}

export default function FabricPaletteForm({
  data,
  onClose,
}: FabricPaletteFormProps) {
  const form = useForm()
  const [editedNames, setEditedNames] = useState<{ [key: number]: string }>({})

  const handleNameChange = useCallback((groupId: number, newName: string) => {
    setEditedNames(prev => ({
      ...prev,
      [groupId]: newName,
    }))
  }, [])

  const columns: ColumnDef<FabricGroup>[] = useMemo(
    () => [
      {
        accessorKey: 'fabric_group.code',
        header: 'Code',
        cell: ({ row }) => row.original.fabric_group?.code || '',
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) =>
          row.original.fabric_group?.fabric_group_profiles[0]?.name ||
          row.original.name ||
          row.original.fabric_group?.code,
      },
      {
        accessorKey: 'custom_name',
        header: 'Custom Name',
        cell: ({ row }) => {
          const groupId = row.original.id
          const initialValue =
            editedNames[groupId] !== undefined
              ? editedNames[groupId]
              : row.original.name || ''

          return (
            <EditableNameInput
              groupId={groupId}
              initialValue={initialValue}
              onNameChange={handleNameChange}
            />
          )
        },
      },
    ],
    [handleNameChange]
  )

  const { data: paletteData } = useQuery(GET_FABRIC_PALETTE, {
    variables: {
      id: data?.id,
    },
    skip: !data?.id,
    onCompleted: data => {
      if (data?.findUniqueFabricPalette) {
        form.reset({
          name: data.findUniqueFabricPalette.name,
          code: data.findUniqueFabricPalette.code,
          fabric_groups_with_names:
            data.findUniqueFabricPalette.fabric_groups.map((group: any) => ({
              fabricGroupId: group.fabric_group.id,
              name: group.name || '',
            })),
        })

        // Initialize editedNames with existing values
        const initialNames: { [key: number]: string } = {}
        data.findUniqueFabricPalette.fabric_groups.forEach((group: any) => {
          if (group.name) {
            initialNames[group.id] = group.name
          }
        })
        setEditedNames(initialNames)
      }
    },
    fetchPolicy: 'network-only',
  })

  const [createFabricPalette] = useMutation(CREATE_FABRIC_PALETTE, {
    refetchQueries: ['GET_FABRIC_PALETTES'],
  })

  const [updateFabricPalette] = useMutation(UPDATE_FABRIC_PALETTE, {
    onError: e => {
      toast.error('Failed to update palette')
    },
    onCompleted: e => {
      toast.success('Fabric group updated palette')
    },
    refetchQueries: ['GET_FABRIC_PALETTES'],
  })

  const onSubmit = async (values: any) => {
    try {
      // Merge form values with edited names
      const fabricGroupsWithNames = fabricGroups.map((group: any) => ({
        fabricGroupId: group.fabric_group.id,
        name:
          editedNames[group.id] !== undefined
            ? editedNames[group.id]
            : group.name || '',
      }))

      const submitData = {
        ...values,
        fabric_groups_with_names: fabricGroupsWithNames,
      }

      if (data) {
        await updateFabricPalette({
          variables: {
            where: { id: data.id },
            ...submitData,
          },
        })
      } else {
        await createFabricPalette({
          variables: submitData,
        })
      }
      onClose?.()
    } catch (error) {
      console.error('Error saving fabric palette:', error)
    }
  }

  const fabricGroups = paletteData?.findUniqueFabricPalette.fabric_groups ?? []

  console.log('fabricGroups', fabricGroups)

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Palette</SheetTitle>
        </div>
        <SheetDescription>
          Id: {data.id} | Updated:{' '}
          {paletteData?.findUniqueFabricPalette.updatedAt}
        </SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter palette name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter palette code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormLabel className="text-left pt-3">Fabric groups</FormLabel>
              <DataTableManual
                columns={columns}
                data={fabricGroups}
                pageIndex={1}
                pageSize={500}
                rowCount={fabricGroups.length}
                onPaginationChange={() => {}}
                onSortingChange={() => {}}
                onColumnFiltersChange={() => {}}
                isMultiSelect={false}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {data ? 'Update' : 'Create'} Fabric Palette
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
